import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { fetchOutrightsAllEventsReq, fetchOutrightsMarketCountReq } from './services';
import { sortEventsByPriority } from 'components/outrights/utils';

const initialState = {
  events: [],
  loading: false,
  error: null,
  selectedSportId: null,
  marketCount: 0
};

export const fetchOutrightsAllEvents = createAsyncThunk(
  'outrights/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const events = await fetchOutrightsAllEventsReq();
      return events;
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to load outrights');
    }
  }
);

export const fetchOutrightsMarketCount = createAsyncThunk(
  'outrights/fetchMarketCount',
  async (_, { rejectWithValue }) => {
    try {
      const count = await fetchOutrightsMarketCountReq();
      return count;
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to load outrights count');
    }
  }
);

const filterActiveMarkets = (events) =>
  events.map((ev) => ({
    ...ev,
    markets: Array.isArray(ev?.markets)
      ? ev.markets.filter((m) => m?.marketStatus === 6)
      : []
  }));

const upsertOutrightEvents = (list, events) => {
  events.forEach((event) => {
    const id = event?.se?.id;
    if (!id) return;
    const idx = list.findIndex((e) => e.se?.id === id);
    if (idx === -1) {
      list.push(event);
    } else {
      list[idx] = event;
    }
  });
};

const outrightsSlice = createSlice({
  name: 'outrights',
  initialState,
  reducers: {
    setSelectedSportId: (state, action) => {
      state.selectedSportId = action.payload;
    },
    clearOutrightsError: (state) => {
      state.error = null;
    },
    addOutrights: (state, action) => {
      const { events: raw = [] } = action.payload || {};
      upsertOutrightEvents(state.events, filterActiveMarkets(raw));
    },
    updateOutrights: (state, action) => {
      const { events: raw = [] } = action.payload || {};
      upsertOutrightEvents(state.events, filterActiveMarkets(raw));
    },
    deleteOutrights: (state, action) => {
      const { events: ids = [] } = action.payload || {};
      const idSet = new Set(ids);
      state.events = state.events.filter((e) => !idSet.has(e.se?.id));
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOutrightsAllEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOutrightsAllEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.events = action.payload;
      })
      .addCase(fetchOutrightsAllEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load outrights';
      })
      .addCase(fetchOutrightsMarketCount.fulfilled, (state, action) => {
        state.marketCount = action.payload;
      });
  }
});

export const {
  setSelectedSportId,
  clearOutrightsError,
  addOutrights,
  updateOutrights,
  deleteOutrights
} = outrightsSlice.actions;

// Base selectors
export const selectAllEvents = (state) => state.outrights.events;
export const selectOutrightsLoading = (state) => state.outrights.loading;
export const selectOutrightsError = (state) => state.outrights.error;
export const selectSelectedSportId = (state) => state.outrights.selectedSportId;
export const selectOutrightsMarketCount = (state) => state.outrights.marketCount;

// Memoized selectors — required to avoid the "selector returned a different result
// when called with the same parameters" warning that triggers extra rerenders.
export const selectFilteredEvents = createSelector(
  [selectAllEvents, selectSelectedSportId],
  (events, selectedSportId) => {
    const filtered =
      selectedSportId == null
        ? events
        : events.filter((e) => e.se?.sportId === selectedSportId);
    return sortEventsByPriority(filtered);
  }
);

export const selectAvailableSports = createSelector(
  [selectAllEvents],
  (events) => {
    const map = new Map();
    for (const e of events) {
      const sId = e.se?.sportId;
      const sName = e.se?.sportName;
      if (sId == null) continue;
      if (!map.has(sId)) map.set(sId, { sportId: sId, sportName: sName, count: 0 });
      map.get(sId).count += 1;
    }
    return Array.from(map.values());
  }
);

// selectEventById returns either an existing array element (stable ref) or null,
// so it does not need memoization.
export const selectEventById = (state, id) =>
  state.outrights.events.find((e) => e.se?.id === id) || null;

export default outrightsSlice.reducer;
